import {
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	OnChanges,
	OnInit,
	Output,
	Renderer2,
	SimpleChanges,
	ViewChild,
	inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { faMinus } from '@fortawesome/pro-solid-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

export enum ValueChangeDirection {
	Increment = 1,
	Decrement = -1,
}

export const inputProps = ['defaultValue', 'placeholder', 'readonly', 'disabled', 'step', 'min', 'max'];

export const numericInputProps = ['min', 'max', 'step'];

// TODO: Use material input instead.

@Component({
	selector: 'g-numeric-input',
	templateUrl: './numeric-input.component.html',
	styleUrls: ['./numeric-input.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(/* istanbul ignore next */ () => NumericInputComponent),
		},
	],
	standalone: false,
})
export class NumericInputComponent implements OnInit, OnChanges, ControlValueAccessor {
	private readonly renderer = inject(Renderer2);

	@ViewChild('numericInput', { static: true }) readonly inputElement: ElementRef<HTMLInputElement>;
	@ViewChild('incrementButton', { static: true }) readonly incrementButton: ElementRef;
	@ViewChild('decrementButton', { static: true }) readonly decrementButton: ElementRef;
	@Input() readonly readonly: boolean;
	@Input() readonly defaultValue: number;
	@Input() readonly unit: string;
	@Input() readonly step = 1;
	@Input() min = -Infinity;
	@Input() max = Infinity;
	@Input() readonly placeholder: string;
	@Output() readonly valueChange = new EventEmitter<number>();
	onChangeFn: (value: number) => void;
	icons = { faPlus, faMinus };

	ngOnInit(): void {
		this.min = Number(this.min);
		this.max = Number(this.max);
		this.setButtonDisabledState(!!this.readonly);
		this.setInputProps();
	}

	ngOnChanges(changes: SimpleChanges): void {
		for (const prop of Object.keys(changes)) {
			if (
				!Object.prototype.hasOwnProperty.call(changes, prop) ||
				changes[prop].currentValue === undefined
			)
				continue;
			const value = changes[prop].currentValue as string;
			if (numericInputProps.includes(prop)) {
				this[prop] = Number.parseFloat(value);
			}
			if (prop === 'readonly') {
				this.setButtonDisabledState(!!value);
			}
			if (inputProps.includes(prop)) {
				this.renderer.setProperty(this.inputElement.nativeElement, prop, value);
			}
		}
	}

	writeValue(value: number): void {
		this.setValue(value, false);
	}

	registerOnChange(fn: (value: number) => void): void {
		this.onChangeFn = fn;
	}

	registerOnTouched(_fn: unknown): void {}

	setDisabledState(isDisabled: boolean): void {
		this.renderer.setProperty(this.inputElement.nativeElement, 'disabled', isDisabled);
		this.setButtonDisabledState(isDisabled);
	}

	onInput(event: DomEvent<HTMLInputElement>): void {
		event.preventDefault();
		event.stopPropagation();
		if (typeof this.onChangeFn === 'function') {
			this.onChangeFn(Number(event.target.value));
		}
		this.valueChange.emit(Number(event.target.value));
	}

	onChange(event: DomEvent<HTMLInputElement>): void {
		event.preventDefault();
		event.stopPropagation();
		if (typeof this.onChangeFn === 'function') {
			this.onChangeFn(Number(event.target.value));
		}
	}

	incrementValue(direction: ValueChangeDirection): void {
		let value = Math.max(Number(this.inputElement.nativeElement.value), this.min);
		value += (this.step ?? 1) * direction;
		value = this.setValue(value);
		this.valueChange.emit(value);
		if (typeof this.onChangeFn === 'function') {
			this.onChangeFn(value);
		}
	}

	private setValue(value: number | string, enforceMinMax = true): number {
		let numericValue = Number(value);
		if (enforceMinMax) {
			numericValue = Math.min(Math.max(Number(numericValue), this.min), this.max);
		}
		this.renderer.setProperty(
			this.inputElement.nativeElement,
			'value',
			value === '' ? value : numericValue,
		);
		return numericValue;
	}

	private setButtonDisabledState(isDisabled: boolean): void {
		this.renderer.setProperty(this.incrementButton.nativeElement, 'disabled', isDisabled);
		this.renderer.setProperty(this.decrementButton.nativeElement, 'disabled', isDisabled);
	}

	private setInputProps(): void {
		for (const prop of inputProps) {
			if (this[prop] !== undefined) {
				this.renderer.setProperty(this.inputElement.nativeElement, prop, this[prop]);
			}
		}
	}
}
